/**
 * Auth hooks — login/logout mutations + bootstrap check on app start.
 *
 * The session token is stored in localStorage and sent as Authorization
 * Bearer on every API request (see api.ts). Cookies are also sent as a
 * fallback but are no longer the primary auth mechanism.
 */
import { useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  apiGet,
  apiPost,
  ApiRequestError,
  setToken,
  clearToken,
  getToken,
} from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { useToast } from '@/stores/toast';
import type { LoginRequest, LoginResponse } from '@ope-v3/shared';

/**
 * On mount, ask the server who we are using the stored bearer token.
 * If no token exists, we stay logged-out and the user lands on /login.
 */
export function useAuthBootstrap(): void {
  const setUser = useAuthStore((s) => s.setUser);
  const setInitialized = useAuthStore((s) => s.setInitialized);
  useEffect(() => {
    let cancelled = false;

    // No token in localStorage → no need to even hit the server
    if (!getToken()) {
      setUser(null);
      setInitialized(true);
      return;
    }

    apiGet<LoginResponse>('/auth/me')
      .then((res) => {
        if (!cancelled) setUser(res.user);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiRequestError && err.status === 401) {
          // Stored token is no longer valid — clear it
          clearToken();
        } else {
          console.error('[auth/me]', err);
        }
        setUser(null);
      })
      .finally(() => {
        if (!cancelled) setInitialized(true);
      });
    return () => {
      cancelled = true;
    };
  }, [setUser, setInitialized]);
}

export function useLoginMutation() {
  const setUser = useAuthStore((s) => s.setUser);
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (vars: LoginRequest) =>
      apiPost<LoginResponse>('/auth/login', vars),
    onSuccess: (res) => {
      // Store the bearer token before setting the user — every subsequent
      // request relies on this token being available in localStorage.
      if (res.token) setToken(res.token);
      setUser(res.user);
      navigate('/');
    },
  });
}

export function useLogoutMutation() {
  const setUser = useAuthStore((s) => s.setUser);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const push = useToast((s) => s.push);
  return useMutation({
    mutationFn: () => apiPost<{ ok: true }>('/auth/logout'),
    onSuccess: () => {
      clearToken();
      setUser(null);
      qc.clear();
      push('ออกจากระบบเรียบร้อย', 'info');
      navigate('/login');
    },
    onError: () => {
      // Even if the server logout call fails, clear local state so the user
      // is signed out on this device.
      clearToken();
      setUser(null);
      qc.clear();
      navigate('/login');
    },
  });
}
